import { Card } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger, /* TabsList */ /* TabsTrigger */ } from "../../ui/tabs";
import {
  BarChart,
  Bar,
  /* PieChart */
  /* Pie */
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useChartColors } from "../../../hooks/useChartColors";
import CustomerLoader from "../../../components/loaders/CustomerLoader";
import { useEcommRegionalAnalysis } from "../../e-commerce/hooks/useEcommRegionalAnalysis";
import { formatCurrency } from "../../../utils/formatCurrency";




export function RetailRegionalDemographics() {
  const { data: regionalData = [], isLoading: isRegionLoading } = useEcommRegionalAnalysis();
  /* const demographicData = generateDemographicData(); */
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  


  useEffect(() => {
    setMounted(true);
  }, []);

  const colors = useChartColors("retail");
  if (!colors) return null;

  const chartColors = {
    grid:
      mounted && theme === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.1)",
    text:
      mounted && theme === "dark"
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(0, 0, 0, 0.7)",
    tooltip: {
      bg:
        mounted && theme === "dark"
          ? "rgba(0, 0, 0, 0.8)"
          : "rgba(255, 255, 255, 0.8)",
      border:
        mounted && theme === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(0, 0, 0, 0.2)",
      text: mounted && theme === "dark" ? "#ffffff" : "#000000",
    },
  };

  const sortedRegionalData = [...regionalData].sort(
    (a, b) => b.customers - a.customers
  );

  return (
    <>
      <SummaryHeader
        title="Top Regions"    // change to  Top Regions & Demographics if demographics endpoint is integrated
      />
      <Card>
        <Tabs defaultValue="chart">
          <TabsList className="mb-4">
            <TabsTrigger value="chart">Chart View</TabsTrigger>
            <TabsTrigger value="table">Table View</TabsTrigger>
          </TabsList>

          <TabsContent value="chart">
            <div className="col-span-1 sm:col-span-3 2xl:col-span-3 flex flex-col">

              <div className="h-[300px]">
                {isRegionLoading ? (
                  <CustomerLoader variant="region-analysis1" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sortedRegionalData}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartColors.grid}
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}
                      />
                      <YAxis
                        dataKey="region"
                        type="category"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: chartColors.tooltip.bg,
                          border: `1px solid ${chartColors.tooltip.border}`,
                          color: chartColors.tooltip.text,
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="customers"
                        name="Customers"
                        fill={colors[2]}
                        radius={[0, 4, 4, 0]}
                      >
                        {sortedRegionalData.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="h-[300px]">
                {isRegionLoading ? (
                  <CustomerLoader variant="region-analysis1" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sortedRegionalData}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartColors.grid}
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}
                        tickFormatter={(value) => `$${value / 1000}k`}
                      />
                      <YAxis
                        dataKey="region"
                        type="category"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `$${value.toLocaleString()}`,
                          "Sales",
                        ]}
                        contentStyle={{
                          backgroundColor: chartColors.tooltip.bg,
                          border: `1px solid ${chartColors.tooltip.border}`,
                          color: chartColors.tooltip.text,
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="sales"
                        name="Sales"
                        fill={colors[0]}
                        radius={[0, 4, 4, 0]}
                      >
                        {sortedRegionalData.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            </TabsContent>
             <TabsContent value="table">      
            <div className="rounded-md border overflow-hidden mt-4">
              {isRegionLoading ? (
                <CustomerLoader variant="region-analysis2" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Region</TableHead>
                      <TableHead className="text-right">Customers</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">
                        Avg. Order Value
                      </TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRegionalData.map((region, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {region.region}
                        </TableCell>
                        <TableCell className="text-right">
                          {region.customers.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(region.sales)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(region.avgOrderValue)}
                        </TableCell>
                        <TableCell className="text-right">
                          {(region.percentOfCustomers).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* <TabsContent value="demographics">
            <div className="grid grid-cols-3 gap-6">
              <div className="h-[300px]">
                <h4 className="font-medium mb-2 text-center">
                  Age Distribution
                </h4>
                {isLoading ? (
                  <CustomerLoader variant="customer-pie" />
                ) : (
                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={demographicData.age}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="percentage"
                        nameKey="group"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {demographicData.age.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value}%`, ""]}
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          borderColor: "var(--border)",
                          borderRadius: "var(--radius)",
                          fontSize: "0.875rem",
                        }}
                        itemStyle={{
                          color: "var(--foreground)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="h-[300px]">
                <h4 className="font-medium mb-2 text-center">
                  Gender Distribution
                </h4>
                {isLoading ? (
                  <CustomerLoader variant="customer-pie" />
                ) : (

                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={demographicData.gender}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="percentage"
                        nameKey="group"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {demographicData.gender.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[(index + 2) % colors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value}%`, ""]}
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          borderColor: "var(--border)",
                          borderRadius: "var(--radius)",
                          fontSize: "0.875rem",
                        }}
                        itemStyle={{
                          color: "var(--foreground)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="h-[300px]">
                <h4 className="font-medium mb-2 text-center">
                  Income Distribution
                </h4>
                {isLoading ? (
                  <CustomerLoader variant="customer-pie" />
                ) : (
                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={demographicData.income}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="percentage"
                        nameKey="group"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {demographicData.income.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[(index + 4) % colors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value}%`, ""]}
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          borderColor: "var(--border)",
                          borderRadius: "var(--radius)",
                          fontSize: "0.875rem",
                        }}
                        itemStyle={{
                          color: "var(--foreground)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Key Demographic Insights</h4>
                {isLoading ? (
                  <CustomerLoader variant="customer-stats" />
                ) : (
                  <ul className="text-muted-foreground space-y-2 text-sm">
                    <li>
                      • Core customer base: 25-44 age group (
                      {demographicData.age[1].percentage +
                        demographicData.age[2].percentage}
                      % of customers)
                    </li>
                    <li>
                      • Gender distribution slightly favors female customers (
                      {demographicData.gender[1].percentage}%)
                    </li>
                    <li>
                      • Middle income brackets ($50K-100K) represent{" "}
                      {demographicData.income[2].percentage +
                        demographicData.income[3].percentage}
                      % of customer base
                    </li>
                    <li>
                      • Opportunity to expand in 55+ age demographic (currently
                      only{" "}
                      {demographicData.age[4].percentage +
                        demographicData.age[5].percentage}
                      %)
                    </li>
                  </ul>
                )}
              </div>

              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Regional Insights</h4>
                {isLoading ? (
                  <CustomerLoader variant="customer-stats" />
                ) : (
                  <ul className="text-muted-foreground space-y-2 text-sm">
                    <li>
                      • North America and Europe account for{" "}
                      {Math.round(
                        ((regionalData[0].sales + regionalData[1].sales) /
                          regionalData.reduce(
                            (sum: number, item: { total_sales: number }) => sum + item.total_sales,
                            0
                          )) *
                        100
                      )}
                      % of total sales
                    </li>
                    <li>
                      • Highest AOV: $
                      {Math.max(...regionalData.map((r: { avgOrderValue: number }) => r.avgOrderValue))} in{" "}
                      {
                        regionalData.find(
                          (r: { avgOrderValue: number }) =>
                            r.avgOrderValue === Math.max(...regionalData.map((r: { avgOrderValue: number }) => r.avgOrderValue))
                        )?.region
                      }
                    </li>
                    <li>• Fastest growing region: Asia Pacific (+18% YoY)</li>
                    <li>
                      • Opportunity markets: Middle East and Latin America showing
                      strong AOV but lower customer counts
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </TabsContent> */}
        </Tabs>
      </Card>
    </>
  );
}
